import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "../../shared/api/api-context.js";
import { requireData } from "../../shared/api/api-error.js";
import { collectPages } from "../../shared/api/collect-pages.js";
import { backtestKeys, type BacktestListFilters } from "../bots/backtest-queries.js";
import { botKeys } from "../bots/queries.js";
import {
  deleteFrozenBacktests,
  filteredDeletionPlan,
  type FilteredDeletionPlan,
} from "./filtered-deletion.js";

export function usePreviewFilteredDeletion(onSettled: () => void) {
  const api = useApi();
  return useMutation({
    retry: false,
    onSettled,
    mutationFn: async (filters: BacktestListFilters) => {
      const page = await collectPages(async (cursor) =>
        requireData(
          await api.GET("/backtests", {
            params: { query: { ...filters, ...(cursor === "" ? {} : { cursor }), limit: 100 } },
          }),
        ),
      );
      return { filtersKey: JSON.stringify(filters), plan: filteredDeletionPlan(page.items) };
    },
  });
}

export function useDeleteFilteredBacktests(
  onProgress: (processed: number) => void,
  onSettled: () => void,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    onSettled,
    mutationFn: (plan: FilteredDeletionPlan) =>
      deleteFrozenBacktests(
        plan.targets,
        async (backtestId) => {
          const result = requireData(
            await api.DELETE("/backtests/{backtestId}", { params: { path: { backtestId } } }),
          );
          if (result.deleted !== true || result.id !== backtestId) {
            throw new Error(
              "Deletion acknowledgement could not be verified. Refresh to check this result.",
            );
          }
          queryClient.removeQueries({ queryKey: backtestKeys.detail(backtestId) });
        },
        onProgress,
      ),
    onSuccess: async (_result, plan) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: backtestKeys.all }),
        ...[...new Set(plan.targets.map((target) => target.botId))]
          .filter((id) => id !== "")
          .map((id) => queryClient.invalidateQueries({ queryKey: botKeys.detail(id) })),
      ]);
    },
  });
}
